
import React, { useState } from 'react';
import SEO from '../components/SEO';
import { trackEvent } from '../components/analytics';

const ContactPage: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    organization: '',
    primaryInterest: 'eAIP Digital Platform',
    message: '',
  });

  const getModuleFromInterest = (interest: string) => {
    const normalized = interest.toLowerCase();
    if (normalized.includes('sms') || normalized.includes('safety')) return 'sms';
    if (normalized.includes('lms') || normalized.includes('training')) return 'lms';
    if (normalized.includes('pans')) return 'pans_ops';
    if (normalized.includes('eaip')) return 'eaip';
    if (normalized.includes('ai')) return 'ai_assistant';
    return 'platform';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      const payload = await response.json();
      if (!payload || payload.code !== 200) {
        throw new Error('Webhook error');
      }

      trackEvent('contact_request', {
        form_id: 'contact_form',
        module: getModuleFromInterest(formData.primaryInterest),
      });
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="animate-fade-in-up">
      <SEO
        title="Contact AviaGov"
        description="Request a personalized demo of the AviaGov aviation platform or speak with our enterprise sales team."
      />

      <section className="py-24 px-4 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
          <div>
            <h1 className="text-5xl font-bold font-heading mb-8">Request a Demo</h1>
            <p className="text-xl text-slate-400 mb-12">
              Ready to modernize your authority's digital services? Our experts will walk you through the platform and show you how to streamline your specific AIRAC and safety workflows.
            </p>

            <div className="space-y-8">
              <div className="flex items-center space-x-6 p-6 rounded-2xl bg-white/5 border border-white/10">
                <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center text-accent">
                   <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                   </svg>
                </div>
                <div>
                   <h4 className="text-white font-bold">Quick Setup</h4>
                   <p className="text-slate-500 text-sm">Cloud or On-Prem deployment in weeks.</p>
                </div>
              </div>
              <div className="flex items-center space-x-6 p-6 rounded-2xl bg-white/5 border border-white/10">
                <div className="w-12 h-12 bg-success/20 rounded-full flex items-center justify-center text-success">
                   <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.040L3 6.247a11.955 11.955 0 003.858 18.255 12.078 12.078 0 0010.284 0 11.955 11.955 0 003.858-18.255l-.618-.103z" />
                   </svg>
                </div>
                <div>
                   <h4 className="text-white font-bold">Compliance First</h4>
                   <p className="text-slate-500 text-sm">Designed specifically for CAA/ANSP regulations.</p>
                </div>
              </div>
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
                <h4 className="text-white font-bold mb-3">AviaGov Address</h4>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Meydan Grandstand, 6th floor, Meydan Road, Nad Al Sheba, Dubai, U.A.E
                </p>
                <div className="mt-4 space-y-1 text-sm text-slate-400">
                  <p>+971 561722090</p>
                  <p>+44 7456456668</p>
                </div>
                <p className="mt-4 text-sm text-slate-400">
                  AviaGov LLC-FZ Trade License No. 2648161.01
                </p>
              </div>
            </div>
          </div>

          <div className="bg-surface p-8 lg:p-12 rounded-[2rem] border border-white/10 relative">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-6 animate-fade-in-up">
                <div className="w-20 h-20 bg-success/20 rounded-full flex items-center justify-center text-success mb-4">
                  <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-3xl font-bold text-white">Message Received</h2>
                <p className="text-slate-400">Our regional head of sales will contact you within 24 hours to schedule your consultation.</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="text-accent font-bold mt-8"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-slate-400 text-xs font-bold uppercase mb-2">First Name</label>
                    <input
                      required
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      className="w-full bg-primary/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-accent transition-colors"
                      placeholder="Jane"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-xs font-bold uppercase mb-2">Last Name</label>
                    <input
                      required
                      type="text"
                      value={formData.surname}
                      onChange={(e) => setFormData((prev) => ({ ...prev, surname: e.target.value }))}
                      className="w-full bg-primary/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-accent transition-colors"
                      placeholder="Doe"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-slate-400 text-xs font-bold uppercase mb-2">Organization</label>
                  <input
                    required
                    type="text"
                    value={formData.organization}
                    onChange={(e) => setFormData((prev) => ({ ...prev, organization: e.target.value }))}
                    className="w-full bg-primary/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-accent transition-colors"
                    placeholder="Civil Aviation Authority of..."
                  />
                </div>
                <div>
                  <label className="block text-slate-400 text-xs font-bold uppercase mb-2">Work Email</label>
                  <input
                    required
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                    className="w-full bg-primary/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-accent transition-colors"
                    placeholder="jane@caa.gov.aero"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 text-xs font-bold uppercase mb-2">Primary Interest</label>
                  <select
                    required
                    className="w-full bg-primary/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-accent transition-colors"
                    value={formData.primaryInterest}
                    onChange={(e) => setFormData((prev) => ({ ...prev, primaryInterest: e.target.value }))}
                  >
                    <option>eAIP Digital Platform</option>
                    <option>Safety Management (SMS)</option>
                    <option>Training & LMS</option>
                    <option>PANS-OPS Design</option>
                    <option>Full Platform Suite</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-400 text-xs font-bold uppercase mb-2">Message</label>
                  <textarea
                    required
                    value={formData.message}
                    onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                    className="w-full bg-primary/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-accent transition-colors h-32 resize-none"
                    placeholder="Tell us about your current digital transformation goals..."
                  ></textarea>
                </div>
                {error ? (
                  <p className="text-sm text-red-400 text-center">{error}</p>
                ) : null}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-5 bg-accent text-primary font-bold rounded-xl hover:shadow-[0_0_30px_rgba(34,211,238,0.4)] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Confirm Request'}
                </button>
                <p className="text-[10px] text-slate-500 text-center uppercase tracking-widest">
                  By submitting you agree to our privacy policy and terms of service.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
